<template>
    <div class="w-full">
        <div class="flex flex-wrap items-center justify-between gap-2">
            <Tabs v-model="client" :options="CLIENTS" class="mb-4" secondary />
            <div class="flex items-center gap-1 text-sm mb-4" role="group" aria-label="Data region">
                <button v-for="r of REGIONS" :key="r" @click="region = r"
                    class="px-3 py-1 rounded-md border border-[var(--light-gray)]"
                    :class="region === r ? 'text-[var(--primary-blue)] border-[var(--primary-blue)]' : 'text-[var(--medium-gray)]'">
                    {{ r }}
                </button>
            </div>
        </div>

        <CopyCode :code="code" :label="CONFIG[client].label" class="mb-4" :page="`Agents ${client}`" />
        <div class="text-sm text-gray-400 mb-4" v-html="CONFIG[client].note" />
        <ExternalLink :href="CONFIG[client].href" :title="CONFIG[client].href_title" />
    </div>
</template>

<script lang="ts">
const MCP_URLS: Record<string, string> = {
    US: 'https://mcp-api.unified.to/mcp',
    EU: 'https://mcp-api-eu.unified.to/mcp',
    AU: 'https://api-au.unified.to/mcp',
};
const REGIONS = Object.keys(MCP_URLS);

// Every snippet reads the workspace API key from an env var or a prompt, so the key never lands in a
// committed config file. The key grants access to the whole Unified.to account.
const CONFIG: Record<string, { label: string; note: string; href: string; href_title: string; code: (url: string) => string }> = {
    'Claude Code': {
        label: 'Claude Code',
        note: 'The plugin installs every Unified.to skill plus the Workspace MCP server, and asks for your API key (kept in secure storage) and your region\'s endpoint. To add only the MCP server, run the <strong>claude mcp add</strong> command in your project with <strong>UNIFIED_API_KEY</strong> set.',
        href: 'https://docs.unified.to/skills',
        href_title: 'Agent Skills and install options',
        code: (url) => `# Skills + Workspace MCP server
/plugin marketplace add unified-to/unified-docs
/plugin install unified@unified-to

# Or only the Workspace MCP server (in your terminal)
claude mcp add --transport http unified-core ${url} \\
  --header "Authorization: bearer $UNIFIED_API_KEY"`,
    },
    Cursor: {
        label: '.cursor/mcp.json',
        note: 'Cursor reads <strong>UNIFIED_API_KEY</strong> from your environment when it starts the server.',
        href: 'https://cursor.com/docs/context/mcp',
        href_title: 'Cursor MCP documentation',
        code: (url) => `{
  "mcpServers": {
    "unified-core": {
      "url": "${url}",
      "headers": {
        "Authorization": "bearer \${env:UNIFIED_API_KEY}"
      }
    }
  }
}`,
    },
    'VS Code': {
        label: '.vscode/mcp.json',
        note: 'GitHub Copilot agent mode prompts for the key once and stores it securely.',
        href: 'https://code.visualstudio.com/docs/copilot/customization/mcp-servers',
        href_title: 'VS Code MCP documentation',
        code: (url) => `{
  "inputs": [
    {
      "type": "promptString",
      "id": "unified-api-key",
      "description": "Unified.to workspace API key",
      "password": true
    }
  ],
  "servers": {
    "unified-core": {
      "type": "http",
      "url": "${url}",
      "headers": {
        "Authorization": "bearer \${input:unified-api-key}"
      }
    }
  }
}`,
    },
    Codex: {
        label: '~/.codex/config.toml',
        note: 'Codex sends <strong>UNIFIED_API_KEY</strong> from your environment as the bearer token. Or run <strong>codex mcp add unified-core --url &lt;endpoint&gt; --bearer-token-env-var UNIFIED_API_KEY</strong>.',
        href: 'https://developers.openai.com/codex/mcp',
        href_title: 'Codex configuration documentation',
        code: (url) => `[mcp_servers.unified-core]
url = "${url}"
bearer_token_env_var = "UNIFIED_API_KEY"`,
    },
    Other: {
        label: 'mcp.json (stdio clients, e.g. Claude Desktop, Windsurf, Zed)',
        note: 'For clients that only speak stdio, <strong>mcp-remote</strong> bridges to the Streamable HTTP endpoint.',
        href: 'https://docs.unified.to/mcp/core',
        href_title: 'Workspace MCP documentation',
        code: (url) => `{
  "mcpServers": {
    "unified-core": {
      "command": "npx",
      "args": ["-y", "mcp-remote", "${url}", "--header", "Authorization:\${AUTH_HEADER}"],
      "env": { "AUTH_HEADER": "bearer <workspace-api-key>" }
    }
  }
}`,
    },
};
const CLIENTS = Object.keys(CONFIG);

export default {
    setup() {
        return {
            CLIENTS,
            REGIONS,
            CONFIG,
        };
    },
    data() {
        return {
            client: CLIENTS[0],
            region: REGIONS[0],
        };
    },
    computed: {
        code(): string {
            return CONFIG[this.client].code(MCP_URLS[this.region]);
        },
    },
};
</script>
